import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { FiSidebar } from 'react-icons/fi';
import { MdSearch, MdClose } from 'react-icons/md';
import styles from './ChatSidebar.module.css';
import ChatHistory from '../chat-history/ChatHistory';
import { ConversationsContext } from '../../context/ConversationsContext';
import { searchConversations } from '../../api/chatApi';
import AlertBox from '../alert-box/AlertBox';
import LoadingIndicator from '../loading-indicator/LoadingIndicator';

const SEARCH_DEBOUNCE_MS = 300;

const ChatSidebar = () => {
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const { conversations, loading, error, refreshConversations } = useContext(ConversationsContext);

    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState(null);
    const [searchError, setSearchError] = useState(null);
    const [isSearching, setIsSearching] = useState(false);
    const debounceTimer = useRef(null);

    const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

    useEffect(() => {
        refreshConversations();
    }, []);

    const handleSearchChange = useCallback((e) => {
        const query = e.target.value;
        setSearchQuery(query);

        if (debounceTimer.current) {
            clearTimeout(debounceTimer.current);
        }

        if (!query.trim()) {
            setSearchResults(null);
            setSearchError(null);
            setIsSearching(false);
            return;
        }

        setIsSearching(true);
        setSearchError(null);
        debounceTimer.current = setTimeout(async () => {
            try {
                const results = await searchConversations(query.trim());
                setSearchResults(results);
                setSearchError(null);
            } catch (err) {
                console.error('Search failed:', err);
                setSearchResults(null);
                setSearchError('Search failed. Please try again later.');
            } finally {
                setIsSearching(false);
            }
        }, SEARCH_DEBOUNCE_MS);
    }, []);

    const clearSearch = useCallback(() => {
        setSearchQuery('');
        setSearchResults(null);
        setSearchError(null);
        setIsSearching(false);
        if (debounceTimer.current) {
            clearTimeout(debounceTimer.current);
        }
    }, []);

    useEffect(() => {
        return () => {
            if (debounceTimer.current) clearTimeout(debounceTimer.current);
        };
    }, []);

    const displayedConversations = searchResults !== null ? searchResults : conversations;
    const isSearchActive = searchResults !== null;

    if (loading) return (
        <div className={`${styles['chat-sidebar']} ${sidebarOpen ? '' : styles['chat-sidebar-closed']}`}>
            <button
                className={styles['sidebar-toggle-button']}
                onClick={toggleSidebar}
                aria-label={sidebarOpen ? 'Close Sidebar' : 'Open Sidebar'}
            >
                <FiSidebar size={24} />
            </button>
            {sidebarOpen && (
                <>
                    <div className={styles['chat-sidebar-title']}>
                        <h2>Conversations</h2>
                    </div>
                    <div className={styles['sidebar-loading-container']}>
                        <LoadingIndicator asMessage={false} error={error} />
                    </div>
                </>
            )}
        </div>
    );

    return (
        <div className={`${styles['chat-sidebar']} ${sidebarOpen ? '' : styles['chat-sidebar-closed']}`}>
            <button
                className={styles['sidebar-toggle-button']}
                onClick={toggleSidebar}
                aria-label={sidebarOpen ? 'Close Sidebar' : 'Open Sidebar'}
            >
                <FiSidebar size={24} />
            </button>
            {sidebarOpen && (
                <>
                    <div className={styles['chat-sidebar-title']}>
                        <span>Conversations</span>
                    </div>
                    {error && <AlertBox message={error} />}
                    <div className={styles['chat-sidebar-search']}>
                        <MdSearch size={18} className={styles['search-icon']} />
                        <input
                            type="text"
                            placeholder="Search conversations..."
                            value={searchQuery}
                            onChange={handleSearchChange}
                        />
                        {searchQuery && (
                            <button
                                className={styles['search-clear-button']}
                                onClick={clearSearch}
                                aria-label="Clear search"
                            >
                                <MdClose size={16} />
                            </button>
                        )}
                    </div>
                    {isSearching && (
                        <div className={styles['search-status']}>Searching...</div>
                    )}
                    {searchError && !isSearching && (
                        <div className={styles['search-status-error']}>{searchError}</div>
                    )}
                    {isSearchActive && !isSearching && !searchError && displayedConversations.length === 0 && (
                        <div className={styles['search-status']}>No conversations found</div>
                    )}
                    <ChatHistory
                        conversations={displayedConversations}
                        searchQuery={isSearchActive ? searchQuery : ''}
                    />
                </>
            )}
        </div>
    );
};

export default ChatSidebar;
